import { Button, HStack, Input, Stack, Text } from '@chakra-ui/react'
import { keepNumberInRange } from '../../../../utils/dataValidation'

interface PartAPercentageInputProps {
  question: string
  value: string
  onChange: (value: string) => void
}

const percentages = Array.from({ length: 11 }, (_, index) => index * 10)

export default function PartAPercentageInput({
  question,
  value,
  onChange,
}: PartAPercentageInputProps) {
  const numericValue = keepNumberInRange(value)

  const setValue = (nextValue: number) => onChange(nextValue.toString())

  return (
    <Stack align='center' spacing={6}>
      <HStack justify='center' flexWrap='wrap' maxW='900px' spacing={3} w='100%'>
        {percentages.map((percentage) => (
          <Button
            aria-pressed={numericValue === percentage}
            colorScheme='purple'
            key={percentage}
            flex='1 1 130px'
            minW='130px'
            onClick={() => setValue(percentage)}
            type='button'
            variant={numericValue === percentage ? 'solid' : 'outline'}
          >
            {percentage}%
          </Button>
        ))}
      </HStack>
      <HStack borderColor='purple.400' borderRadius='lg' borderWidth='1px' h='64px' overflow='hidden' spacing={0}>
        <Input
          aria-label={`${question} percentage`}
          bg='white'
          border='0'
          borderRadius='0'
          fontSize='2xl'
          h='full'
          min={0}
          onChange={(event) => {
            const nextValue = event.target.value
            const normalizedValue = keepNumberInRange(nextValue)
            onChange(
              nextValue === '' || normalizedValue === null
                ? ''
                : normalizedValue.toString(),
            )
          }}
          type='number'
          value={numericValue ?? ''}
          w='240px'
          px={6}
          py={0}
        />
        <Stack borderLeftWidth='1px' borderRightWidth='1px' h='full' spacing={0} w='84px'>
          <Button
            aria-label='Increase percentage'
            borderRadius='0'
            flex={1}
            fontSize='2xl'
            minH='unset'
            onClick={() => setValue(Math.min((numericValue ?? 0) + 1, 100))}
            type='button'
            variant='ghost'
          >
            +
          </Button>
          <Button
            aria-label='Decrease percentage'
            borderRadius='0'
            borderTopWidth='1px'
            flex={1}
            fontSize='2xl'
            minH='unset'
            onClick={() => setValue(Math.max((numericValue ?? 0) - 1, 0))}
            type='button'
            variant='ghost'
          >
            −
          </Button>
        </Stack>
        <Text alignItems='center' alignSelf='stretch' bg='purple.50' display='flex' fontSize='lg' justifyContent='center' px={6}>
          %
        </Text>
      </HStack>
      <Text color='gray.500' textAlign='center'>
        You can select a percentage above or enter a more precise value here.
      </Text>
    </Stack>
  )
}
